export default class App {
	constructor() {
		this._resizeFunctionsArr = [];
		this._resizeFunctions = {};
		this._resizeFunctionsCounter = 0;
		this._resizeTimeout = null;
		this.resizeThrottle = false;

		this._scrollFunctionsArr = [];
		this._scrollFunctions = {};
		this._scrollFunctionsCounter = 0;
		this._scrollTimeout = null;
		this.scrollThrottle = false;

		this._resizeAnimationPreventerTimeout = 200;

		this.breakpoints = {
			xs: {
				gutter: 10,
				size: 0,
			},
			sm: {
				gutter: 30,
				size: 375,
			},
			md: {
				gutter: 30,
				size: 768,
			},
			lg: {
				gutter: 30,
				size: 1024,
			},
			xl: {
				gutter: 30,
				size: 1440,
			},
		}
	}

	init() {
		this.detectBrowser();
		this.setEventListeners();
		this.fullHeightListener();
		this.resizeAnimationPreventer();
		this.addNewWindowTitles();
		this.addNoOpener();
		this.initSelectric();
		this.initRoofScanSelectric();
		this.initTippy();
		this.initScrollTo();
		this.initScrollToTop();
		this.initLabelClick();
		this.initClickID();

		return true;
	}

	setEventListeners() {
		window.addEventListener('resize', this.resize.bind(this), {
			passive: true
		});
		document.addEventListener('scroll', this.scroll.bind(this), {
			passive: true
		});

		document.querySelectorAll('main form label').forEach(label => {
			label.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'ent') {
					label.click();
				}
			});
		});
	}

	resize() {
		const runFunctions = () => {
			for (let i = 0, length = this._resizeFunctionsArr.length; i < length; i++) {
				this._resizeFunctionsArr[i]();
			}
		};
		if (this._resizeTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.resizeThrottle) {
			this._resizeTimeout = setTimeout(() => {
				this._resizeTimeout = null;
				runFunctions();
			}, this.resizeThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a resize event
	 */
	registerResizeEvent(func) {
		this._resizeFunctions[this._resizeFunctionsCounter] = func;
		this._resizeFunctionsCounter = this._resizeFunctionsCounter + 1;

		// Rebuild array of events
		this._resizeFunctionsArr = [];
		Object.keys(this._resizeFunctions).forEach(key => {
			this._resizeFunctionsArr.push(this._resizeFunctions[key]);
		});

		return this._resizeFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the resize functions
	 */
	unregisterResizeEvent(func) {
		if (this._resizeFunctions[func]) {
			// Found
			delete this._resizeFunctions[func];

			// Rebuild array of events
			this._resizeFunctionsArr = [];
			Object.keys(this._resizeFunctions).forEach(key => {
				this._resizeFunctionsArr.push(this._resizeFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Fires all registered events for a scroll event in the document
	 */
	scroll() {
		const runFunctions = () => {
			for (let i = 0, length = this._scrollFunctionsArr.length; i < length; i++) {
				this._scrollFunctionsArr[i]();
			}
		};
		if (this._scrollTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.scrollThrottle) {
			this._scrollTimeout = setTimeout(() => {
				this._scrollTimeout = null;
				runFunctions();
			}, this.scrollThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a scroll event
	 */
	registerScrollEvent(func) {
		this._scrollFunctions[this._scrollFunctionsCounter] = func;
		this._scrollFunctionsCounter = this._scrollFunctionsCounter + 1;

		// Rebuild array of events
		this._scrollFunctionsArr = [];
		Object.keys(this._scrollFunctions).forEach(key => {
			this._scrollFunctionsArr.push(this._scrollFunctions[key]);
		});

		return this._scrollFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the scroll functions
	 */
	unregisterScrollEvent(func) {
		if (this._scrollFunctions[func]) {
			// Found
			delete this._scrollFunctions[func];

			// Rebuild array of events
			this._scrollFunctionsArr = [];
			Object.keys(this._scrollFunctions).forEach(key => {
				this._scrollFunctionsArr.push(this._scrollFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Adds a 'preventAnimations' class to the html tag on resizing
	 */
	resizeAnimationPreventer() {
		this.resizeTimer = null;
		this.registerResizeEvent(() => {
			document.documentElement.classList.add('preventAnimations');
			clearTimeout(this.resizeTimer);

			this.resizeTimer = setTimeout(() => {
				document.documentElement.classList.remove('preventAnimations');
			}, this._resizeAnimationPreventerTimeout);
		});
	}

	/**
	 * @description Adds a CSS variable '--fullHeight' to the html tag that creates an 'accurate 100vh'
	 */
	fullHeightListener() {
		const fullHeight = () => {
			document.documentElement.style.setProperty('--fullHeight', `${window.innerHeight}px`);
		};
		this.registerResizeEvent(fullHeight);
		fullHeight();
	}

	/**
	 * @description Adds a browser-specific class to the html tag
	 */
	detectBrowser() {
		// Opera 8.0+
		let isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

		// Firefox 1.0+
		let isFirefox = typeof InstallTrigger !== 'undefined';

		// Safari 3.0+ "[object HTMLElementConstructor]" 
		let isSafari = /constructor/i.test(window.HTMLElement) || (function (p) {
			return p.toString() === "[object SafariRemoteNotification]";
		})(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

		// Internet Explorer 6-11
		let isIE = /*@cc_on!@*/ false || !!document.documentMode;

		// Edge 20+
		let isEdge = !isIE && !!window.StyleMedia;

		// Chrome 1 - 71
		let isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

		// Blink engine detection
		let isBlink = (isChrome || isOpera) && !!window.CSS;

		if (isOpera) {
			document.documentElement.classList.add('opera');
		}

		if (isFirefox) {
			document.documentElement.classList.add('firefox');
		}

		if (isSafari) {
			document.documentElement.classList.add('safari');
		}

		if (isIE) {
			document.documentElement.classList.add('ie');
		}

		if (isEdge) {
			document.documentElement.classList.add('edge');
		}

		if (isChrome) {
			document.documentElement.classList.add('chrome');
		}

		if (isBlink) {
			document.documentElement.classList.add('blink');
		}
	}

	/**
	 * @description Appends text to a tags that tells the user it'll open in a new tab/window. Add a tag with `data-new-window-text` property to change the appended text
	 */
	addNewWindowTitles() {
		let text = '(opent in een nieuw scherm)';
		if (document.querySelector('[data-new-window-text]')) {
			text = document.querySelector('[data-new-window-text]').dataset.newWindowText;
		}

		document.querySelectorAll('a[target="_blank"]').forEach(a => {
			a.setAttribute('title', `${a.getAttribute('title')} ${text}`.trim().replace(/  /g, " "));
		});
	}

	/**
	 * @description Adds the attribute 'rel' set to 'noopener' on external links; This is for security reasons/best practices
	 */
	addNoOpener() {
		document.querySelectorAll('a').forEach(a => {
			if (a.href && !a.hasAttribute('rel')) {
				if (a.hostname != location.hostname) {
					a.setAttribute('rel', 'noopener');
				}
			}
		});
	}

	/**
	 * @description Scroll element into view
	 */
	initScrollTo() {
		document.querySelectorAll('[data-scroll-to]').forEach(el => {
			el.addEventListener('click', e => {
				const target = document.querySelector(el.dataset.scrollTo);

				if (target && target.scrollIntoView) {
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'start',
					});
				}
			});
		});
	}

	initScrollToTop() {
		this.scrollToTopContainer = document.querySelector('.scrollToTop');

		if (this.scrollToTopContainer) {
			const button = this.scrollToTopContainer.querySelector('button');
			const scroll = () => {
				if (document.documentElement.scrollTop > 400) {
					this.scrollToTopContainer.classList.add('active');
				} else {
					this.scrollToTopContainer.classList.remove('active');
				}
			};

			this.registerScrollEvent(scroll.bind(this));

			button.addEventListener('click', () => {
				this.scrollToTopContainer.classList.add('moving');
				button.blur();
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
				setTimeout(() => this.scrollToTopContainer.classList.remove('moving'), 1500);
			});
		}
	}

	initSelectric() {
		if (document.querySelectorAll('main select').length) {
			try {
				import( /* webpackChunkName: "modules/dropdown" */ 'selectric').then(e => {
					$('main select').selectric({
						arrowButtonMarkup: '<svg class="sprite icon_chevron_down button"><use xlink:href="#icon_chevron_down"></use></svg>',
					});
				});
			} catch (e) {
				console.error(e);
			}
		}
	}

	initRoofScanSelectric() {
		if (document.querySelectorAll('.roofscanSelectContainer select').length) {
			try {
				import( /* webpackChunkName: "modules/dropdown" */ 'selectric').then(e => {
					$('.roofscanSelectContainer select').selectric({
						arrowButtonMarkup: '<svg class="sprite icon_chevron_down button"><use xlink:href="#icon_chevron_down"></use></svg>',
						disableOnMobile: false, 
						nativeOnMobile: false,
						optionsItemBuilder: function(itemData, element, index) {
							if (itemData.value == 'custom') {
								return '<span class="ico icon-' + itemData.value +  '"><svg class="sprite icon_' + itemData.value + '"><use xlink:href="#icon_' + itemData.value + '" /></svg></span><span class="roofscanValue">Weet ik niet</span>';
							} else {
								return itemData.value.length ? '<span class="ico icon-' + itemData.value +  '"><svg class="sprite icon_' + itemData.value + '"><use xlink:href="#icon_' + itemData.value + '" /></svg></span><span class="roofscanValue">' + itemData.value + ' kWh</span>' : itemData.text;
							}
						}
					});
				});
			} catch (e) {
				console.error(e);
			}
		}
	}

	/**
	 * @description Tooltip library
	 */
	initTippy() {
		if (document.querySelectorAll('[data-tippy-content]').length) {
			try {
				import( /* webpackChunkName: "modules/tooltip" */ 'tippy.js').then(e => {
					const tippy = e.default;
					tippy('[data-tippy-content]');
				});
			} catch (e) {
				console.error(e);
			}
		}

	}

	/**
	 * @description Makes tags with the data-copy property clickable, copies its content to the clipboard
	 */
	initCopy() {
		document.querySelectorAll('[data-copy]').forEach(el => {
			el.addEventListener('click', () => {
				try {
					const copyContent = el.dataset.copy;
					const input = document.createElement('input');
		
					input.type = 'text';
					input.style.position = 'absolute';
					input.style.height = 0;
					input.style.overflow = 'hidden';
					input.value = copyContent;
		
					document.body.appendChild(input);
					input.select();
					document.execCommand('copy');
					input.parentNode.removeChild(input);
				} catch (e) {
					this.toast('Something unexpected went wrong', 'error');
					console.error(e);
				}
			});
		});
	}

	/**
	 * @description Makes sure labels that target an input become accessible using keyboard controls
	 */
	initLabelClick() {
		document.querySelectorAll('label[for]').forEach(label => {
			label.addEventListener('keydown', e => {
				switch (e.key.toLowerCase()) {
					case ' ':
						e.preventDefault();
					case 'space':
					case 'enter':
						label.click();
						break;
				}
			});
		});
	}

	/**
	 * @description Checks for and saves potential Click IDs. Makes it possible to send the ID alongside the roofscanner.
	 */
	initClickID() {
		this.clickID = null;
		let UrlClickID = this.getParameter(`gclid`);

		if (UrlClickID) {
			this.clickID = UrlClickID;

			if (window.sessionStorage) {
				// Save to Session Storage
				window.sessionStorage.setItem(`clickID`, this.clickID);
			}
		} else if (window.sessionStorage) {
			// Try to retrieve from Session Storage
			if (window.sessionStorage.getItem(`clickID`)) {
				this.clickID = window.sessionStorage.getItem(`clickID`);
			}
		}
	}

	getParameter(name) {
		let result = null,
			tmp = [];
		let items = location.search.substr(1).split("&");
		for (let index = 0; index < items.length; index++) {
			tmp = items[index].split("=");
			if (tmp[0] === name) {
				result = decodeURIComponent(tmp[1]);
			}
			if (result === 'undefined') {
				result = true;
			}
		}
		return result;
	}

	insertTesters() {
		this.insertBreakpointIndicator();
		this.insertColumnTester();
	}

	insertColumnTester() {
		let tester = document.createElement('div');
		tester.classList.add('columnTester');
		let cols = ``;
		for (let i = 0; i < 12; i++) {
			cols += `<div class="col-1"><div></div></div>`;
		}
		tester.innerHTML = `<div class="container"><div class="row">${cols}</div></div>`;
		document.body.appendChild(tester);
	}

	insertBreakpointIndicator() {
		const breakpoints = Object.keys(this.breakpoints);
		let blocks = ``;
		breakpoints.forEach((breakpoint, index) => {
			let classes = 'col ';
			if (index == 0) {
				classes += `d-${breakpoints[index + 1]}-none`;
			} else if (index == breakpoints.length - 1) {
				classes += `d-none d-${breakpoint}-block`;
			} else {
				classes += `d-none d-${breakpoint}-block d-${breakpoints[index + 1]}-none`;
			}
			blocks += `<div class="${classes}"><div class="breakpointIndicator">${breakpoint}</div></div>`;
		});


		let tester = document.createElement('div');
		tester.classList.add('breakpointIndicators');

		tester.innerHTML = `<div class="container"><div class="row">${blocks}</div></div>`;
		document.body.appendChild(tester);
	}
}